import { ROUTES } from '@dashboard/router/routeNames'
import { useConfigStore } from '@dashboard/stores/configStore'
import { nextTick } from 'vue'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

// The retired Open-Ended Responses page filtered by an "Other" segment ID.
// Map that link onto the question that owns the segment's dimension.
const legacySurveyResponsesRedirect = async (to: RouteLocationNormalized): Promise<RouteLocationRaw> => {
  const target = { name: ROUTES.RESPONSES, params: to.params }
  const segmentId = typeof to.query.segmentId === 'string' ? to.query.segmentId : null
  if (!segmentId) {
    return target
  }

  // The layout that normally loads the team's config hasn't mounted yet, so load
  // the requested team here (as the auth middleware does) before looking it up.
  const teamPid = Number.parseInt(String(to.params.teamPid), 10)
  const configStore = useConfigStore()
  try {
    if (configStore.currentTeamPid !== teamPid || configStore.currentTeam?.pid !== teamPid) {
      configStore.currentTeamPid = teamPid
      await configStore.configQuery.refetch()
      await nextTick()
    }
  } catch {
    return target
  }
  const config = configStore.currentTeam?.pid === teamPid ? configStore.config : null
  if (!config) {
    return target
  }
  const dimension = config.dimensions.find((d) => d.segments.some((segment) => segment.id === segmentId))
  const question = dimension
    ? config.rightAsk?.questions.find((candidate) => candidate.dimensionId === dimension.id)
    : undefined

  return question ? { ...target, query: { questionId: question.id, other: 'true' } } : target
}

export default [
  {
    name: ROUTES.GROWTH_IDEAS,
    path: 'growth-ideas',
    component: () => import('./pages/GrowthIdeas.vue'),
    meta: {
      title: 'Growth Ideas',
      description: 'AI-powered content ideas and growth strategies based on your audience data.',
      requiredFeature: 'full_analytics'
    }
  },
  {
    name: ROUTES.OPTIMIZATIONS,
    path: 'optimizations',
    component: () => import('./pages/Optimizations.vue'),
    meta: {
      title: 'Optimizations',
      description: 'Build your next improvement with Righty.'
    }
  },
  {
    name: ROUTES.RESPONSES,
    path: 'responses',
    component: () => import('./pages/Responses.vue'),
    meta: {
      title: 'Responses',
      description: "Everything visitors typed: answers to text questions and 'Other' write-ins.",
      requiredFeature: 'full_analytics',
      rightyPrompts: [
        {
          label: 'Pain points',
          drawerLabel: 'Uncover audience pain points',
          prompt:
            'Analyze the open-ended survey responses to identify the top pain points my audience is experiencing. What are they struggling with most?'
        },
        {
          label: 'Goals',
          drawerLabel: 'Discover audience goals and aspirations',
          prompt:
            'Based on the survey responses and visitor data, what are the primary goals and aspirations of my audience? What are they trying to achieve?'
        },
        {
          label: 'Sentiment',
          drawerLabel: 'Analyze audience sentiment and language',
          prompt:
            'Analyze the sentiment in my survey responses. What themes emerge? How are people feeling about their challenges and what language are they using?'
        },
        {
          label: 'Content gaps',
          drawerLabel: 'Find gaps in my content strategy',
          prompt:
            'Based on what my audience is asking and talking about, what content am I missing? What topics should I create content around?'
        }
      ]
    }
  },
  {
    // A redirect record skips beforeEnter, so this record keeps a component and
    // always leaves through the guard's returned location.
    path: 'survey-responses',
    component: () => import('./pages/Responses.vue'),
    beforeEnter: legacySurveyResponsesRedirect
  },
  // Keep old route for backwards compatibility (redirects)
  {
    name: ROUTES.INSIGHTS,
    path: 'insights',
    redirect: { name: ROUTES.GROWTH_IDEAS }
  }
]
